import 'rc-slider/assets/index.css';
import {
  faBracketRoundedSquareLeft, faBracketRoundedSquareRight,
} from '@awesome.me/kit-77e1f5874f/icons/kit/custom';
import { css } from '@emotion/react';
import Slider from 'rc-slider';
import { type MarkObj } from 'rc-slider/lib/Marks';
import {
  type CSSProperties, forwardRef, type ReactNode, useCallback, useEffect, useMemo,
  useState,
} from 'react';
import type { Writable } from 'ts-essentials';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { useDebounce } from '~/_shared/utils/hooks/useDebounce';
import { isNumber } from '~/_shared/utils/typeGuards';
import { useTheme } from '../../themes/theme.hooks';
import { type ThemeProps } from '../../types/themeProps';
import { isArray } from '../../utils/collections/collections';

export type SliderMarks = Record<string | number, ReactNode | MarkObj>;

export enum SliderConstraintType {
  GreaterThan = 'greaterThan',
  LessThan = 'lessThan',
}

export type SliderConstraint = {
  type: SliderConstraintType;
  value: number;
};

const DEFAULT_HANDLE_COLOR = '#fff';

const outOfConstraintHandleStyle = ({ theme }: ThemeProps): CSSProperties => ({
  backgroundColor: theme.sliderColors.danger,
  transition: 'background .2s',
});

const handleStyle = ({ theme, index }: ThemeProps<{ index: number }>): CSSProperties => ({
  backgroundColor: index % 2 === 0 ? theme.sliderColors.contrastColor : DEFAULT_HANDLE_COLOR,
});

const constraintMarkIconContainerStyle = ({ isGreaterThan }: { isGreaterThan: boolean }): CSSProperties => ({
  marginLeft: isGreaterThan ? 2 : -2,
});

const constraintMarkIconStyle = ({ theme, index }: ThemeProps<{ index: number }>) => css({
  color: index % 2 === 0 ? theme.sliderColors.contrastColor : DEFAULT_HANDLE_COLOR,
  fontSize: 14,
});

const constraintDotStyle: CSSProperties = {
  display: 'none',
  pointerEvents: 'none',
};

export type SliderValue = readonly [number, ...number[]];

type SliderProps = Readonly<{
  className?: string;
  constraints?: SliderConstraint | SliderConstraint[]; //constraints need to be in the same order as value, take priority over marks and dots
  delay?: number;
  dotStyle?: CSSProperties | ((value: number) => CSSProperties);
  included?: boolean;
  disabled?: boolean;
  marks?: SliderMarks;
  max: number;
  min: number;
  reverse?: boolean;
  step: number;
  value: SliderValue;
  vertical?: boolean;
  withDots?: boolean;
  children?: React.ReactNode;
  onChange: (value: SliderValue) => void;
}>;

const sliderStyle = ({ theme, isVertical, disabled, marksOnLine }: ThemeProps<{ isVertical?: boolean; disabled?: boolean; marksOnLine?: boolean }>) => {
  const widthOrHeightProp = isVertical ? { width: 4 } : { height: 4 };

  return css({
    '.rc-slider-track': {
      background: theme.sliderColors.trackBackground,
      ...widthOrHeightProp,
    },
    '.rc-slider-rail': {
      background: theme.sliderColors.railBackground,
      ...widthOrHeightProp,
    },
    '.rc-slider-dot': {
      backgroundColor: theme.sliderColors.dotColor,
      borderColor: theme.sliderColors.dotBorder,
      cursor: 'default',
    },
    '.rc-slider-dot-active': {
      backgroundColor: theme.sliderColors.activeDotColor,
      borderColor: theme.sliderColors.activeDotBorder,
    },
    '.rc-slider-handle': {
      '&, &.rc-slider-handle-dragging': {
        boxShadow: `0px 3px 7px ${theme.sliderColors.handleShadow}`,
        border: 'none',
        cursor: 'default',
        opacity: 1,
        backgroundColor: DEFAULT_HANDLE_COLOR,
        zIndex: 1,
      },
    },
    '&.rc-slider-disabled': {
      backgroundColor: 'inherit',
    },
    ...(marksOnLine ? {
      '.rc-slider-mark': {
        top: 0,
        pointerEvents: 'none',
      },
    } : {}),

    opacity: disabled ? theme.opacity.disabled : undefined,
  });
};

export const SliderComponent = forwardRef<HTMLDivElement, SliderProps>(({
  children,
  className,
  constraints,
  delay,
  disabled,
  dotStyle,
  included,
  marks,
  max,
  min,
  onChange,
  reverse,
  step,
  value,
  vertical,
  withDots,
  ...restProps
}, ref) => {
  const theme = useTheme();

  const [internalValue, setInternalValue] = useState<SliderValue>(value);
  const onChangeDebounced = useDebounce(onChange, delay || 500);

  const changeHandler = useCallback((values: number | [number, ...number[]]) => {
    const finalValue = isArray(values) ? values : [values] as const;
    setInternalValue(finalValue);
    if (delay) {
      onChangeDebounced(finalValue);
    }
    else {
      onChange(finalValue);
    }
  }, [delay, onChange, onChangeDebounced]);

  useEffect(() => {
    setInternalValue(value);
  }, [value, onChangeDebounced]);

  const constraintsMarksAndStyles = useMemo(() => {
    if (!constraints) {
      return null;
    }

    return (isArray(constraints) ? constraints : [constraints]).reduce((acc, constraint, index) => {
      acc.marks[acc.marks[constraint.value] ? constraint.value + 0.0001 : constraint.value] = {
        label: (
          <FontAwesomeIcon
            key={constraint.type === SliderConstraintType.GreaterThan
              ? 'dash-left' : 'dash-right'}
            icon={constraint.type === SliderConstraintType.GreaterThan
              ? faBracketRoundedSquareLeft : faBracketRoundedSquareRight}
            css={constraintMarkIconStyle({ theme, index })}
          />
        ),
        style: constraintMarkIconContainerStyle({ isGreaterThan: constraint.type === SliderConstraintType.GreaterThan }),
      };
      acc.handleStyle.push(
        (
          (internalValue[index] && constraint.type === SliderConstraintType.GreaterThan && (internalValue[index] ?? 0) < constraint.value)
          || (internalValue[index] && constraint.type === SliderConstraintType.LessThan && (internalValue[index] ?? 0) > constraint.value)
        ) ? outOfConstraintHandleStyle({ theme }) : handleStyle({ theme, index })
      );
      return acc;
    }, { handleStyle: [] as React.CSSProperties[], marks: {} as SliderMarks });
  }, [constraints, internalValue, theme]);

  const [singleValue] = internalValue;

  return (
    <div
      {...restProps}
      ref={ref}
      className={className}
    >
      {value.length === 1 && isNumber(singleValue) && (
        <Slider
          range={false}
          css={sliderStyle({
            theme,
            isVertical: vertical,
            disabled,
            marksOnLine: !!constraintsMarksAndStyles,
          })}
          disabled={disabled}
          dotStyle={constraintsMarksAndStyles?.marks ? constraintDotStyle : dotStyle}
          dots={withDots}
          included={included}
          marks={constraintsMarksAndStyles?.marks || marks}
          max={max}
          min={min}
          onChange={changeHandler}
          reverse={reverse}
          step={step}
          value={singleValue}
          vertical={vertical}
          handleStyle={constraintsMarksAndStyles?.handleStyle[0] || undefined}
        />
      )}

      {value.length > 1 && (
        <Slider
          range
          allowCross={false}
          css={sliderStyle({ theme, disabled, marksOnLine: !!constraintsMarksAndStyles })}
          disabled={disabled}
          dotStyle={constraintsMarksAndStyles?.marks ? constraintDotStyle : dotStyle}
          marks={constraintsMarksAndStyles?.marks || marks}
          max={max}
          min={min}
          onChange={changeHandler}
          step={step}
          value={value as Writable<SliderValue>}
          handleStyle={constraintsMarksAndStyles?.handleStyle.length
            ? constraintsMarksAndStyles?.handleStyle : undefined
          }
        />
      )}

      {children}
    </div>
  );
});
